// oxlint-disable react/jsx-pascal-case
/**
 * Folio premium Select built on `@kobalte/core`.
 *
 * Same pattern as `popover.tsx` / `dialog.tsx` / `checkbox.tsx`: re-exports
 * Kobalte parts already styled with the repo's tokens. Inherits from Kobalte
 * full keyboard navigation (arrows, Home/End, Esc), typeahead, ARIA roles
 * (listbox/option), and focus management. Panel enters with opacity ONLY
 * (`.ui-select-content` → keyframe `ui-select-in` in `global.css`): never
 * animates `transform`, because panel positioning is done by @floating-ui
 * with a `transform` and animating transform would override it for a frame (flicker on open).
 * Automatically respects the global `prefers-reduced-motion` guard.
 *
 * Generic usage:
 *   <Select
 *     options={["a", "b"]}
 *     value={value()}
 *     onChange={setValue}
 *     itemComponent={(props) => (
 *       <SelectItem item={props.item}>{props.item.rawValue}</SelectItem>
 *     )}
 *   >
 *     <SelectTrigger>
 *       <SelectValue<string>>{(state) => state.selectedOption()}</SelectValue>
 *     </SelectTrigger>
 *     <SelectContent />
 *   </Select>
 */
import {
  type SelectContentProps,
  type SelectItemProps,
  Select as SelectPrimitive,
  type SelectTriggerProps,
} from "@kobalte/core/select";
import { type JSX, Show, splitProps } from "solid-js";
import { Check, ChevronDown, Plus } from "@fleet/icons";
import { cn } from "@folio/shared";

export const Select = SelectPrimitive;
export const SelectValue = SelectPrimitive.Value;
export const SelectLabel = SelectPrimitive.Label;

/**
 * CTA rendered inside the dropdown when the Select has no options (AWS-style:
 * an empty combobox offers "Create X" / "Go to X" instead of a dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or `href` to render
 * a link (navigate to the screen that provides the options). Provide exactly one.
 * `can` (default `true`) gates the CTA on permission — when `false` the empty
 * dropdown falls back to a plain "no options" message and no creation affordance
 * is shown. Omit `emptyAction` entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true. */
  can?: boolean;
  /** Leading icon for the CTA. Defaults to a plus glyph. */
  icon?: JSX.Element;
  /** Optional helper line shown above the CTA (e.g. "No notebooks yet"). */
  description?: string;
}

export function SelectTrigger(
  props: SelectTriggerProps & { class?: string; children?: JSX.Element },
) {
  const [local, others] = splitProps(props, ["class", "children"]);
  return (
    <SelectPrimitive.Trigger
      class={cn(
        "select-trigger group flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm outline-none transition-[border-color,box-shadow,background-color] duration-150",
        "hover:border-primary/50 hover:bg-accent/5",
        "focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring",
        "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
        "data-[invalid]:border-destructive data-[invalid]:ring-destructive/30",
        local.class,
      )}
      {...others}
    >
      <span class="min-w-0 flex-1 truncate text-left">{local.children}</span>
      <SelectPrimitive.Icon class="shrink-0 text-muted-foreground/70 transition-transform duration-200 group-data-[expanded]:rotate-180">
        <ChevronDown class="h-4 w-4" aria-hidden="true" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectContent(
  props: SelectContentProps & {
    class?: string;
    /**
     * Flags the Select as having no options. When `true`, panel displays the
     * `emptyAction` CTA (create / go) instead of an empty Listbox. Because this
     * primitive is a thin re-export (Listbox self-populates from Root and
     * does not expose `options`), emptiness is determined by the call site.
     */
    isEmpty?: boolean;
    /** Contextual CTA when source is creatable. See {@link SelectEmptyAction}. */
    emptyAction?: SelectEmptyAction;
    /** Message when no CTA is permitted (or source is not creatable). */
    emptyLabel?: string;
  },
) {
  const [local, others] = splitProps(props, ["class", "isEmpty", "emptyAction", "emptyLabel"]);
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        class={cn(
          "ui-select-content z-[250] overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg outline-none",
          local.class,
        )}
        {...others}
      >
        <Show
          when={!local.isEmpty}
          fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
        >
          {/* La Listbox se autopuebla desde la colección del Root vía `itemComponent`. */}
          <SelectPrimitive.Listbox class="max-h-72 overflow-y-auto overscroll-contain p-1 [scrollbar-gutter:stable]" />
        </Show>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the `emptyAction` CTA
 * (creation button or navigation link) when present and permitted, otherwise a
 * muted "no options" message. The CTA closes the dropdown on activation.
 *
 * The CTA is keyboard-reachable (it is a native button/anchor, auto-focusable
 * inside the portalled panel) and inherits the panel's opacity-only entrance, so
 * it respects `prefers-reduced-motion` via the global guard like the rest of the UI.
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent/15 hover:text-foreground",
    "focus-visible:bg-accent/15 focus-visible:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-primary" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-2.5 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays i18n-clean; the
              neutral em-dash is the last-resort placeholder when no label is given. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1 p-1">
          <Show when={action().description}>
            <p class="px-2.5 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}

export function SelectItem(props: SelectItemProps & { class?: string; children?: JSX.Element }) {
  const [local, others] = splitProps(props, ["class", "children"]);
  return (
    <SelectPrimitive.Item
      class={cn(
        "relative flex cursor-pointer select-none items-center gap-2 rounded-md py-2 pl-3 pr-8 text-sm outline-none transition-colors duration-100",
        "data-[highlighted]:bg-accent/15 data-[highlighted]:text-foreground",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "data-[selected]:font-medium data-[selected]:text-primary",
        local.class,
      )}
      {...others}
    >
      <SelectPrimitive.ItemLabel class="min-w-0 flex-1 truncate">
        {local.children}
      </SelectPrimitive.ItemLabel>
      <SelectPrimitive.ItemIndicator class="absolute right-2.5 flex items-center justify-center text-primary motion-safe:animate-checkmark-pop">
        <Check class="h-4 w-4" aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}
